/**
 * Reading the links PithPlay emails out (password reset, editor invitation)
 * before the page asks the user for anything.
 *
 * Both tokens are `payload.signature`, with a base64url JSON payload that
 * says what the link is for and when it expires. The signature can only be
 * checked by the API, which stays the authority: these checks only catch
 * links that are certain to fail (cut off, the wrong kind, expired), so the
 * user hears about it before typing a new password twice, not after.
 */

/**
 * The way back from the password pages. Signed-in people reach them from
 * Profile's "Change password" (or open the emailed link while signed in), so
 * "Back to sign in" would be wrong for them.
 */
export const backLinkFor = (signedIn: boolean): { label: string; to: string } =>
  signedIn ? { label: 'Back to profile', to: '/profile' } : { label: 'Back to sign in', to: '/login' };

const decodeBase64Url = (value: string): string => {
  let base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = base64.length % 4;
  if (padding) base64 += '='.repeat(4 - padding);
  return atob(base64);
};

const readPayload = (encoded: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(encoded));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/**
 * Leeway for a fast clock on the user's device: a link this close to its
 * expiry is left for the API to judge, so nobody is told a working link has
 * expired.
 */
export const LINK_CLOCK_SKEW_MS = 2 * 60 * 1000;

/**
 * Whether a signed link's own expiry has passed. The API answers an expired
 * link the same way as a broken one; this lets the page say which it is.
 */
export const hasSignedTokenExpired = (token: string, now: number = Date.now()): boolean => {
  const [encoded, signature] = token.split('.');
  if (!encoded || !signature) return false;
  const payload = readPayload(encoded);
  return Boolean(payload && typeof payload.expiresAt === 'number' && now > payload.expiresAt);
};

export type ResetLinkProblem = 'invalid' | 'expired';

/**
 * Whether a `/reset-password?token=` token can possibly work. The API
 * (resetPasswordWithToken + secureTokenUtils) only accepts signed tokens of
 * type `password_reset`, so anything else is invalid.
 */
export const checkResetToken = (token: string, now: number = Date.now()): ResetLinkProblem | null => {
  const parts = token.split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return 'invalid';
  const payload = readPayload(parts[0]);
  if (!payload || payload.type !== 'password_reset') return 'invalid';
  if (typeof payload.expiresAt === 'number' && now > payload.expiresAt + LINK_CLOCK_SKEW_MS) return 'expired';
  return null;
};

export type InvitationLinkProblem = 'incomplete' | 'invalid' | 'expired' | 'wrong-email' | 'wrong-type';

/**
 * Checks an editor invitation link in the order the page always has: the
 * token can be read and isn't expired, it was sent to this address, and it
 * is an invitation. Accepts the signed format and the older plain base64 one.
 */
export const checkInvitationLink = (
  token: string | null,
  email: string | null,
  now: number = Date.now()
): InvitationLinkProblem | null => {
  if (!token || !email) return 'incomplete';

  let payload: Record<string, unknown> | null;
  if (token.includes('.')) {
    const [encoded, signature] = token.split('.');
    payload = encoded && signature ? readPayload(encoded) : null;
  } else {
    try {
      const parsed: unknown = JSON.parse(atob(token));
      payload = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      payload = null;
    }
  }

  if (!payload || typeof payload.email !== 'string' || !payload.email) return 'invalid';
  if (typeof payload.expiresAt === 'number' && payload.expiresAt < now) return 'expired';
  if (payload.email !== email) return 'wrong-email';
  if (payload.type && payload.type !== 'editor_invitation') return 'wrong-type';
  return null;
};

export interface LinkProblemCopy {
  title: string;
  description: string;
}

export const INVITATION_PROBLEM_COPY: Record<InvitationLinkProblem, LinkProblemCopy> = {
  incomplete: {
    title: "This invitation link isn't complete",
    description:
      "Open the link in your invitation email again. If it still doesn't work, ask the creator to send a new invitation.",
  },
  invalid: {
    title: "This invitation link isn't valid",
    description: 'Ask the creator who invited you to send a new invitation.',
  },
  expired: {
    title: 'This invitation has expired',
    description: 'Invitations last 7 days. Ask the creator who invited you to send a new one.',
  },
  'wrong-email': {
    title: 'This invitation is for a different email address',
    description: 'Open the invitation that was sent to your email address, or ask the creator to invite you again.',
  },
  'wrong-type': {
    title: "This link isn't an editor invitation",
    description: 'Open the invitation email again, or ask the creator who invited you to send a new invitation.',
  },
};
